import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import VoiceBioNotice from './VoiceBioNotice';
import { useToast } from '../../hooks/use-toast';
import {
  getActivationStatus,
  acceptCreatorAgreement,
  activateCreator,
} from '../../api/creator_onboarding';

/**
 * Step 3 · Creator Terms acceptance + activation.
 * The Audio Avatar remains a backend requirement for activation:
 * a warning with instructions is shown until it is uploaded.
 */
const StepActivation = ({ onActivated }) => {
  const { t } = useTranslation('onboarding');
  const { toast } = useToast();

  const [statusLoading, setStatusLoading] = useState(true);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [checked, setChecked] = useState(false);
  const [audioAvatarOk, setAudioAvatarOk] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const s = await getActivationStatus();
        setAgreementAccepted(!!s.agreement_accepted);
        setChecked(!!s.agreement_accepted);
        setAudioAvatarOk(!!s.audio_avatar_ok);
      } catch (_e) { /* soft */ } finally {
        setStatusLoading(false);
      }
    })();
  }, []);

  const errMsg = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === 'string') return d.includes('|') ? d.split('|')[1] : d;
    return fallback;
  };

  const handleActivate = async () => {
    setBusy(true);
    try {
      if (!agreementAccepted) {
        await acceptCreatorAgreement();
        setAgreementAccepted(true);
      }
      await activateCreator();
      toast({ title: t('activation_step.activated_title') });
      onActivated?.();
    } catch (e) {
      toast({ title: t('activation_step.activate_error'), description: errMsg(e, 'Riprova'), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> …
      </div>
    );
  }

  const canActivate = checked && audioAvatarOk;

  return (
    <div data-testid="wizard-step-activation">
      <h2 className="text-xl font-semibold text-foreground">{t('activation_step.title')}</h2>
      <p className="mt-1 mb-5 text-sm text-muted-foreground">{t('activation_step.subtitle')}</p>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 gold-text" />
          <p className="text-sm font-semibold text-foreground">{t('activation_step.agreement_block_title')}</p>
        </div>
        <label className="mt-3 flex cursor-pointer items-start gap-3">
          <Checkbox
            checked={checked}
            onCheckedChange={(v) => setChecked(!!v)}
            disabled={agreementAccepted}
            className="mt-0.5"
            data-testid="agreement-checkbox"
          />
          <span className="text-sm leading-relaxed text-muted-foreground">
            {t('activation_step.agreement_pre')}{' '}
            <Link
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold gold-text underline underline-offset-2"
              data-testid="agreement-terms-link"
            >
              {t('activation_step.agreement_link')}
            </Link>
            {t('activation_step.agreement_post')}
          </span>
        </label>
      </div>

      {!audioAvatarOk ? (
        <VoiceBioNotice className="mt-4" />
      ) : (
        <p className="mt-4 flex items-center gap-1.5 text-xs font-medium text-emerald-300" data-testid="voice-bio-done">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> {t('activation_step.voice_bio_done')}
        </p>
      )}

      <Button
        type="button"
        onClick={handleActivate}
        disabled={!canActivate || busy}
        className="mt-6 w-full gold-gradient text-black sm:w-auto"
        data-testid="activate-creator-btn"
      >
        {busy
          ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('activation_step.activating')}</>
          : t('activation_step.activate_cta')}
      </Button>
    </div>
  );
};

export default StepActivation;
